import { useIntl } from '@capsicum/common';
import { Tooltip } from '@capsicum/common-web';
import { InfoIcon } from '@capsicum/common-web/icons';
import { UkRiskLink } from '@capsicum/features/regulations/web';
import { useWalletCurrencies } from '@capsicum/features/wallet/shared';
import { Regulation } from '@capsicum/socios-core';
import styled, { useTheme } from 'styled-components';

export enum WalletTooltipType {
	BALANCE = 'balance',
	LOCKED = 'locked'
}

export type WalletTooltipsProps = {
	type: WalletTooltipType;
};

export const WalletTooltips = ({ type }: WalletTooltipsProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();
	const { data } = useWalletCurrencies();
	const isUsaRegulation = data?.regulation === Regulation.Usa;

	const tooltipsTexts = {
		...(type === WalletTooltipType.BALANCE && {
			checkbox: $t('wallet.walletBalance.tooltipCheckbox'),
			message: isUsaRegulation
				? $t('wallet.walletBalance.tooltipBalanceMessageUsa')
				: $t('wallet.walletBalance.tooltipBalanceMessage'),
			title: isUsaRegulation
				? $t('wallet.walletBalance.tooltipBalanceTitleUsa')
				: $t('wallet.walletBalance.tooltipBalanceTitle')
		}),
		...(type === WalletTooltipType.LOCKED && {
			message: isUsaRegulation
				? $t('wallet.walletBalance.tooltipLockedMessageUsa')
				: $t('wallet.walletBalance.tooltipLockedMessage'),
			title: $t('wallet.walletBalance.tooltipLockedTitle', {
				amount: data?.defaultCurrency?.lockedBalance ?? '',
				code: data?.defaultCurrency?.shortCode ?? ''
			})
		})
	};

	const BalanceTooltip = () => {
		return (
			<Tooltip contentPosition="bottom_right">
				<Tooltip.Trigger testID={`wallet-header-tooltipTrigger${type}`}>
					<S.InfoIconStyled color={colors.icons.info.enabled} height="16" width="16" />
				</Tooltip.Trigger>
				<Tooltip.Content testID={`wallet-header-tooltipContent${type}`}>
					<Tooltip.Title>{tooltipsTexts.title}</Tooltip.Title>
					<Tooltip.Description>{tooltipsTexts.message}</Tooltip.Description>
					<UkRiskLink iconSize={20} page="wallet" />
				</Tooltip.Content>
			</Tooltip>
		);
	};

	const LockedTooltip = () => {
		return (
			<Tooltip contentPosition="bottom_right">
				<Tooltip.Trigger testID={`wallet-header-tooltipTrigger${type}`}>
					<S.InfoIconStyled color={colors.icons.info.enabled} height="16" width="16" />
				</Tooltip.Trigger>
				<Tooltip.Content testID={`wallet-header-tooltipContent${type}`}>
					<Tooltip.Title>{tooltipsTexts.title}</Tooltip.Title>
					<Tooltip.Description>{tooltipsTexts.message}</Tooltip.Description>
				</Tooltip.Content>
			</Tooltip>
		);
	};

	switch (type) {
		case WalletTooltipType.BALANCE:
			return <BalanceTooltip />;
		case WalletTooltipType.LOCKED:
			return <LockedTooltip />;
		default:
			return null;
	}
};

namespace S {
	export const InfoIconStyled = styled(InfoIcon)`
		flex-shrink: 0;
	`;
}
